import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { InteractablePopover } from 'ui-components/components/interactable-popover';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { useIsDocumentUploadEnabled } from '../../../../../feature-flags/hooks/use-is-document-upload-enabled';
import Global from '../../../../../store/global-selectors';
import { useFulfillmentPlanIdContext } from '../../../../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import { useActiveFulfillmentIdsForFulfillmentPlan } from '../../../../../hooks/entities/selectors/fulfillments';
import { useModal } from '../../../../../hooks/use-modal';
import { useSalesOrderIdContext } from '../../../../context/sales-order-id-context-provider';
import RepeatButton from '../../../../../components/repeat-button';
import { savePendingDocumentConfigRequest } from '../../../../../store/customs-documents/customs-documents.actions';
import { useCustomsDocuments } from './use-customs-documents';
import { CustomsDocumentUploadModal } from './customs-document-upload-modal';
import { OrderDocumentListItem } from './order-document-list-item';
import { DocumentUploadForm } from './document-upload-form';
import { getIncompatibleDocumentTypeLabels } from './document-type-dropdown';
import { CUSTOMS_DOCUMENTS_HELP_URL, messages } from './messages';
import type { CustomsDocument } from './types';
import style from './customs-documents-section.module.less';

/**
 * Customs document upload section in Order Details Canvas.
 *
 * Pre-ship states:
 * - **starter**: description + "Upload file" button + defer checkbox (initial view)
 * - **ready/initialized**: full upload form (dropdown + drop zone + file list)
 *
 * Post-ship states:
 * - **ready/initialized/sending/done**: handled by DocumentUploadForm
 *
 * Warning when carrier does not support upload is non-blocking -- upload UI is still shown.
 */
export const CustomsDocumentsSection = () => {
  const isDocumentUploadEnabled = useIsDocumentUploadEnabled();
  const intl = useIntl();
  const fulfillmentPlanId = useFulfillmentPlanIdContext();
  const salesOrderId = useSalesOrderIdContext();
  const fulfillmentIds = useActiveFulfillmentIdsForFulfillmentPlan(fulfillmentPlanId);
  const shipmentId = fulfillmentIds[0] ?? null;

  const dispatch = useDispatch();
  // Our store is the source of truth after user toggles; CSW state is the fallback for initial load.
  const pendingFromOurStore = useSelector((state) => Global.selectCustomsDocumentsPendingDocuments(state, fulfillmentPlanId));
  const customs = useSelector((state) => Global.selectCSWCustoms(state, { fulfillmentPlanId }));
  const isDeferringUpload = pendingFromOurStore ?? customs?.pendingDocuments ?? false;
  const { isOpen: isModalOpen, open: openModal, close: closeModal } = useModal();

  const {
    isLoadingCapabilities,
    isCarrierSupported,
    availableDocumentTypes,
    carrierCodeForUpload,
    trackingNumber,
    orderDocuments,
    isSending,
    handleRefresh,
    handleSendToCarrier,
  } = useCustomsDocuments({
    salesOrderId,
    shipmentId,
    fulfillmentPlanId,
  });

  const handleDeferChange = useCallback(
    (checked: boolean) => {
      dispatch(savePendingDocumentConfigRequest(fulfillmentPlanId!, checked));
    },
    [dispatch, fulfillmentPlanId],
  );

  const incompatibleTypeLabels = getIncompatibleDocumentTypeLabels(intl, orderDocuments, availableDocumentTypes);

  if (!isDocumentUploadEnabled || isLoadingCapabilities) {
    return null;
  }

  // ── PRE-SHIP: no label created yet ──────────────────────────────────────────
  if (!shipmentId) {
    return (
      <FlexColumn className={style['section-content']} gap={Gap.Space16}>
        <FlexRow className={style['section-header']} gap={Gap.Space8}>
          <Text textType={TextType.H4} value={messages.sectionTitle} />
          <RepeatButton
            actionHandler={handleRefresh}
            ariaLabelMessageDescriptor={messages.refreshAriaLabel}
            className={style['refresh-icon']}
          />
        </FlexRow>
        {!isCarrierSupported && (
          <BannerMessageV2
            content={
              <Text
                textType={TextType.Paragraph}
                value={orderDocuments.length > 0 ? messages.carrierNotSupportedWithDocs : messages.carrierNotSupported}
              />
            }
            layout={BannerLayout.Default}
            status={BannerStatus.Warning}
          />
        )}
        {isCarrierSupported && incompatibleTypeLabels.length > 0 && (
          <BannerMessageV2
            content={
              <Text
                formattedMessageValues={{ types: incompatibleTypeLabels.join(', ') }}
                textType={TextType.Paragraph}
                value={messages.documentTypesNotSupported}
              />
            }
            layout={BannerLayout.Default}
            status={BannerStatus.Warning}
          />
        )}
        {!isDeferringUpload && (
          <FlexColumn className={style['starter-content']} gap={Gap.Space12}>
            <Text
              className={style['starter-description']}
              textType={TextType.Paragraph}
              value={messages.starterDescription}
              formattedMessageValues={{
                learnMoreLink: (chunks) => (
                  <Button href={CUSTOMS_DOCUMENTS_HELP_URL} target='_blank' rel='noopener noreferrer' theme={THEME_CONSTANTS.link}>
                    {chunks}
                  </Button>
                ),
              }}
            />
            <Button
              disabled={!isCarrierSupported}
              disabledTooltipMessage={<FormattedMessage {...messages.uploadDisabledTooltip} />}
              onClick={openModal}
              theme={THEME_CONSTANTS.secondary}
            >
              <FormattedMessage {...messages.starterUploadFileButton} />
            </Button>
          </FlexColumn>
        )}
        {orderDocuments.length > 0 && (
          <>
            <Text formattedMessageValues={{ count: orderDocuments.length }} textType={TextType.H4} value={messages.uploadedFilesHeading} />
            <Text className={style['info-note']} textType={TextType.Caption} value={messages.uploadedFilesInfoNote} />
            <ul className={style['document-list']}>
              {orderDocuments.map((doc: CustomsDocument) => (
                <li key={doc.documentId}>
                  <OrderDocumentListItem document={doc} salesOrderId={salesOrderId} />
                </li>
              ))}
            </ul>
          </>
        )}
        {isModalOpen && (
          <CustomsDocumentUploadModal
            availableDocumentTypes={availableDocumentTypes}
            isDisabled={!isCarrierSupported}
            onClose={closeModal}
            onRefresh={handleRefresh}
            salesOrderId={salesOrderId}
          />
        )}
        {(orderDocuments.length === 0 || isDeferringUpload) && (
          <FlexColumn className={style['defer-section']} gap={Gap.Space4}>
            <FlexRow className={style['defer-checkbox-row']} gap={Gap.Space8}>
              <Checkbox
                checked={isDeferringUpload}
                label={intl.formatMessage(messages.pendingDocumentsCheckbox)}
                onChange={handleDeferChange}
              />
              <InteractablePopover
                popoverBody={
                  <FlexColumn gap={Gap.Space8}>
                    <Text textType={TextType.Paragraph} value={messages.pendingDocumentsPopoverBody} />
                    <Button href={CUSTOMS_DOCUMENTS_HELP_URL} theme={THEME_CONSTANTS.link}>
                      <FormattedMessage {...messages.starterLearnMoreLink} />
                    </Button>
                  </FlexColumn>
                }
                preferPlace='bottom'
                title={<FormattedMessage {...messages.pendingDocumentsPopoverTitle} />}
              >
                <FontAwesomeIcon className={style['info-icon']} icon={faCircleInfo} size='sm' />
              </InteractablePopover>
            </FlexRow>
            <Text className={style['pending-note']} textType={TextType.Caption} value={messages.pendingDocumentsNote} />
          </FlexColumn>
        )}
      </FlexColumn>
    );
  }

  // ── POST-SHIP: label created, working with shipment documents ───────────────
  return (
    <FlexColumn className={style['section-content']} gap={Gap.Space16}>
      <FlexRow className={style['section-header']} gap={Gap.Space8}>
        <Text textType={TextType.H4} value={messages.sectionTitle} />
        <RepeatButton
          actionHandler={handleRefresh}
          ariaLabelMessageDescriptor={messages.refreshAriaLabel}
          className={style['refresh-icon']}
        />
      </FlexRow>
      <FlexColumn className={style['starter-content']} gap={Gap.Space12}>
        <Button
          disabled={!isCarrierSupported}
          disabledTooltipMessage={<FormattedMessage {...messages.uploadDisabledTooltip} />}
          onClick={openModal}
          theme={THEME_CONSTANTS.secondary}
        >
          <FormattedMessage {...messages.starterUploadFileButton} />
        </Button>
      </FlexColumn>
      {isModalOpen && (
        <CustomsDocumentUploadModal
          availableDocumentTypes={availableDocumentTypes}
          carrierCode={carrierCodeForUpload}
          isDisabled={!isCarrierSupported}
          isSending={isSending}
          onClose={closeModal}
          onRefresh={handleRefresh}
          onSubmit={handleSendToCarrier}
          shipmentId={shipmentId}
          trackingNumber={trackingNumber}
        />
      )}
      <DocumentUploadForm
        availableDocumentTypes={availableDocumentTypes}
        carrierCode={carrierCodeForUpload}
        initialState='notInitialized'
        isDisabled={!isCarrierSupported}
        isSending={isSending}
        onSubmit={handleSendToCarrier}
        shipmentId={shipmentId}
        trackingNumber={trackingNumber}
      />
    </FlexColumn>
  );
};
